import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router, NavigationExtras } from '@angular/router';
import { BehaviorSubject, Subscription, of, Observable } from 'rxjs';
import { finalize, mergeMap, tap } from 'rxjs/operators';
//import { PDFDocumentProxy } from 'pdfjs-dist';

import { ChiefsOrderService } from '../../../../services/chiefsOrder.service';
import { DialogService } from '../../../../common/services/dialog.service';
import { LoaderService } from '../../../../common/services/loader.service';
import { ReportSelectorService } from '../../../../form-controls/services/report-selector.service';

import { GenerateReportModel } from '../../../../reports/model/reports.model';
import { levelContext } from '../../../../models/comodel/chiefsorderdetailsmodel.model';
import { DynamicDialogService } from '../../../../core/services/dynamic-dialog.service';
import { Dialog, DialogButton } from '../../../../core/models/dialog.model';
import { Group } from '../../../models/group.model';
import { GroupService } from '../../../services/group.service';
import { ChiefsOrderMergeConstants } from '../../../../models/comodel/mergefieldsmodel.model';
import { DocumentManagementService, IUploadFile, UploadedFile } from '../../../../common/services/document-management.service';
import { AuthService } from '../../../../services/auth.service';
import { UploadViewFilesComponent } from '../../../../common/components/fileUpload/upload-view-files.component';

@Component({
    selector: 'co-submit-chiefsorder',
    templateUrl: './co-submit-chiefsorder.component.html',
    styleUrls: ['./co-submit-chiefsorder.component.scss'],
    standalone: false
})
export class CoSubmitChiefsorderComponent implements OnInit,OnDestroy {

    confirmFileDeleteDialog: Dialog = new Dialog("Are you sure you want to delete this file?", [new DialogButton("Delete the file", true, () => this.onFileDeleteConfirm()), new DialogButton("Cancel", true, () => { })]);
    successDialog: Dialog = new Dialog("The Chief's Order was successfully submitted to the next level of review.", [new DialogButton("OK", true, () => this.onSubmitSuccessDialogConfirm())]);
    selectAReviewGroupReminderDialog: Dialog = new Dialog("Please select a valid review group and try again.", [new DialogButton("OK", true, () => this.onSelectAReviewGroupReminderDialogConfirm())]);

    @ViewChild("files") public _uploadViewFilesElement: UploadViewFilesComponent;
    @ViewChild("supplementalFiles") public _uploadViewSupplementalFilesElement: UploadViewFilesComponent;
    @ViewChild("downloadLink") public downloadLink: ElementRef;

    currentPage: number = 1;
    coInstanceId: string;
    pdfAvailable: boolean = false;
    pdfData: any;
    pdfUrl: string;
    title: string;

    private reportModel: GenerateReportModel = {
        useBanner: true,
        usePageNumberHeader: true,
        formatAsLetter: true,
        headerOnFirstPage: false,
        htmlHeader: "",
        html: "",
        htmlFooter: "",
        styleMergeFields: true
    } as GenerateReportModel;
    showError: boolean = false;
    subscription = new Subscription();
    submitModel: levelContext = {};
    totalPages: number = 0;


    displayGroupAssignment: boolean = false;
    allGroups: Group[] = [];
    selectedGroup: Group = {
        id: "0",
        displayName: "---------- Please Select a Group ----------",
        active: true,
        groupType: "placeholder",
        permissions: [],
        users: []
    };

    get filesToShow(): UploadedFile[] {
        if (!this.submitModel.files)
            this.submitModel.files = [];

        return this.submitModel.files;
    }

    get supplementalFilesToShow(): UploadedFile[] {
        if (!this.submitModel.postIssueFiles)
            this.submitModel.postIssueFiles = [];

        return this.submitModel.postIssueFiles;
    }

    fileToDelete: IUploadFile;

    private _isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    get isLoading$(): Observable<boolean> {
        return this._isLoading$.asObservable();
    }

    constructor(
        private activeRoute: ActivatedRoute,
        public authService: AuthService,
        private docMgmtSrvc: DocumentManagementService,
        private chiefsOrderService: ChiefsOrderService,
        private readonly loader: LoaderService,
        public reportService: ReportSelectorService,
        private router: Router,
        public dialogService: DynamicDialogService,
        public groupService: GroupService
    ) { }

    ngOnInit() {
        this.loader.toggleLoader(true);
        this.coInstanceId = this.activeRoute.snapshot.params["coid"];
        
        const pdf$ = this.chiefsOrderService.getlettercontents(this.coInstanceId).pipe(
            tap((contents) => {
                this.submitModel = contents;
                this.displayGroupAssignment = contents.data.nextLevelOfReviewRequiresGroupSelection;
                this.reportModel.usePageNumberHeader = this.submitModel.data.editedLetter.usePageNumberHeader;
                this.reportModel.mergeFields = contents.data.fields;
                this.reportModel.htmlHeader = this.submitModel.data.editedLetter.htmlHeader;
                this.reportModel.html = this.submitModel.data.editedLetter.htmlContent;
                this.reportModel.htmlFooter = this.submitModel.data.editedLetter.htmlFooter;
                this.title = this.submitModel.data.templateName;
            }),
            mergeMap(() => {
                //If we need to display groups for selecting next level, fetch them
                if (this.displayGroupAssignment) {
                    return (this.groupService.get({ stubs: false }) as Observable<Group[]>).pipe(
                        tap((groups) => {
                            if (groups) {
                                groups.unshift(this.selectedGroup);
                                this.allGroups = groups;
                            }
                        })
                    );
                } else {
                    return of(null);
                }
            }),
            mergeMap(() => {
                if (this.reportModel.html && this.reportModel.html !== "") {
                    return this.chiefsOrderService.generateChiefsOrderDocument(this.reportModel).pipe(
                        tap((blob) => {
                            if (blob) {
                                this.pdfUrl = window.URL.createObjectURL(blob);
                                this.pdfData = blob;
                            }
                        })
                    );
                } else {
                    return of(null);
                }
            }),
            finalize(() => { this.subscription.add(pdf$); })
        ).subscribe((data) => {
            this.pdfAvailable = data !== null;
            this.loader.toggleLoader(false);
            this._isLoading$.next(false);
        }, (error) => {
            console.error(error);
            this.showError = true;
            this.loader.toggleLoader(false);
        });
    }

    ngOnDestroy(): void {
        if (this.subscription) this.subscription.unsubscribe();
    }

    download(): void {
        const blob = new Blob([this.pdfData], { type: "application/pdf" });
        const fileName = `order-preview.pdf`;
		const url = URL.createObjectURL(blob);
		const link = this.downloadLink.nativeElement;
		link.href = url;
		link.download = fileName;
		link.click();
    }

    goToLetter(): void {
        let res: NavigationExtras = {
            queryParams: { prev: "true" }
        }
        this.router.navigate(['/admin/chief-orders/co-approve-letter'],res);
    }

    nextPage(): void {
        if (this.currentPage >= this.totalPages) return;
        this.currentPage++;
    }

/*onLoadComplete(pdf: PDFDocumentProxy): void {*/
	onLoadComplete(pdf: any): void {
        this.totalPages = pdf.numPages;
    }

    previousPage(): void {
        if (this.currentPage <= 1) return;
        this.currentPage--;
    }

    submitCO(): void {
        this.loader.toggleLoader(true);
        var coInstanceId = this.submitModel.id;

        if (!this.displayGroupAssignment || (this.displayGroupAssignment && this.selectedGroup.id != "0")) {
           
            this.submitModel.data.userSelectedGroup = this.selectedGroup;

            var srv$ = this.chiefsOrderService.saveletter(this.submitModel).pipe(mergeMap(() => {
                return this.chiefsOrderService.submitco(coInstanceId).pipe(finalize(() => { this.subscription.add(srv$); }))
            })).subscribe(result => {
                this.loader.toggleLoader(false);
                if (result) {
                    if (window.sessionStorage.getItem('levelInfo')) {
                        window.sessionStorage.removeItem('levelInfo')
                    }
                    if (window.sessionStorage.getItem('coInfo')) {
                        window.sessionStorage.removeItem('coInfo');
                    }
                    

                    this.successDialog.message = `The "${this.submitModel.data.templateName}" Order was submitted to the "${result.assignedToGroup}" group for review`;
                    this.successDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.successDialog);
                }
            }, error => {
                this.loader.toggleLoader(false);
                console.log('failed to save...' + error);
            });
        }
        else {
            this.selectAReviewGroupReminderDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.selectAReviewGroupReminderDialog);
        }
    }

    public onSubmitSuccessDialogConfirm(): void {
        this.router.navigate(["/dashboard/view/ricsDashboard"]);
    }

    onSelectAReviewGroupReminderDialogConfirm() {
        this.selectAReviewGroupReminderDialog.toggleDialog(false);
        this.dialogService.updateDialog(this.selectAReviewGroupReminderDialog);
        this.loader.toggleLoader(false);
    }

    savedFiles($event: IUploadFile) {
        var newFile = $event;
        this.filesToShow.push(newFile as UploadedFile);
    }

    public hasFileListing(): boolean {
        return this.filesToShow.length > 0 ? true : false;
    }

    public allowFileDeletion(file: IUploadFile): boolean {
        return (this.authService.User.id === file.uploadedByUserId || this.authService.User.organizationId === file.uploadedByOrganizationId);
    }

    deleteFile($event: IUploadFile) {
        this.fileToDelete = $event;
        this.confirmFileDeleteDialog.message = `Are you sure you want to delete this file?`;
        this.confirmFileDeleteDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.confirmFileDeleteDialog);
    }

    downloadFile($event: IUploadFile) {
        var file = $event;
        const download$ = this.docMgmtSrvc.downloadFile(file.blobUri).pipe(
            tap((data) => {
                const link = this.downloadLink.nativeElement;
                link.href = data;
                link.click();
            }),
            finalize(() => {
                this.subscription.add(download$);
            })
        ).subscribe(() => { });
    }

    public onFileDeleteConfirm() {
        this.loader.toggleLoader(true);

        var abandon$ = this.docMgmtSrvc.abandon(this.fileToDelete).pipe(
            tap((results) => {
                const index = this.filesToShow.findIndex(x => x.blobUri === this.fileToDelete.blobUri);
                if (index !== -1) {
                    this.filesToShow.splice(index, 1);
                    //Now make sure the file got removed from the upload-view-files component too
                    this._uploadViewFilesElement.removeFile(this.fileToDelete);
                }
                else {
                    const index = this.supplementalFilesToShow.findIndex(x => x.blobUri === this.fileToDelete.blobUri);
                    if (index !== -1) {
                        this.supplementalFilesToShow.splice(index, 1);
                        this._uploadViewSupplementalFilesElement.removeFile(this.fileToDelete);
                    }
                }
            }),
            mergeMap(() => {

                return this.chiefsOrderService.deleteFilesFromContext(this.fileToDelete, this.coInstanceId).pipe(
                    tap((groups) => {
                        let contextFileIndex = this.submitModel.files.findIndex(x => x.blobUri === this.fileToDelete.blobUri);
                        if (contextFileIndex !== -1) {
                            this.submitModel.files.splice(contextFileIndex, 1);
                        }
                        else {
                            const contextFileIndex = this.submitModel.postIssueFiles.findIndex(x => x.blobUri === this.fileToDelete.blobUri);
                            if (contextFileIndex !== -1)
                                this.submitModel.postIssueFiles.splice(contextFileIndex, 1);
                        }
                        console.log('file was deleted: ', this.fileToDelete.fileName);
                    })
                );
            }),
            finalize(() => { this.subscription.add(abandon$); }))
            .subscribe(() => {

                this.loader.toggleLoader(false);

            }, (error) => { console.error(error); this.loader.toggleLoader(false); });
    }

    savedSupplementalFiles($event: IUploadFile) {
        var newFile = $event;
        this.supplementalFilesToShow.push(newFile as UploadedFile);
    }

    public hasSupplementalFileListing(): boolean {
        return this.supplementalFilesToShow.length > 0 ? true : false;
    }
}
